// SECURITY: signed URLs are bearer credentials — never log them.
// This helper generates short-lived signed URLs for the `terms-documents`
// bucket (and other private buckets) with auto-renew on expiry/403.
//
// Contract pinned by Phase 7-02 RED test
// (`tests/terms/unit/signed-url-renewal.test.ts`):
//   - expiresIn must be >= 7200 (2h Storage decision floor)
//   - refresh() returns a NEW URL distinct from the previous one
//   - refresh() invokes onRenew(prevUrl) BEFORE swapping closure state
//   - the URL string is NEVER passed to console.log/console.error
//
// Phase 9-04 extension: optional `useEdgeFunction` flag routes signed-URL
// minting through the `get-terms-signed-url` Edge Function (Option A bucket
// gateway — auditable + admin-only SELECT preserved). Default `false` to
// preserve the legacy direct-storage path that the 09-01 unit tests assume.
// Callers in Phase 9 (PdfViewer, DynamicTermsViewer iframe, TermsHistoryTab
// download) opt in by passing `useEdgeFunction: true`.
//
// Implementation notes:
//   - Stateless w.r.t. React (testable node-side, reusable in EF code)
//   - In-flight dedupe Map<key, Promise> coalesces parallel refresh() calls
//     to a single network round-trip (iframe + canvas dual-consumer scenario
//     in Plan 09-02)
import { supabase } from '@/integrations/supabase/client';

const TTL_FLOOR_SECONDS = 7200; // 2h — Storage decision in 09-CONTEXT.md

export interface CreateSignedUrlOptions {
  bucket: string;
  path: string;
  /** Seconds until URL expiry. MUST be >= 7200 (2h Storage decision floor). */
  expiresIn: number;
  /** Invoked with the previous URL right before the closure swaps to the new one. */
  onRenew?: (previousUrl: string) => void;
  /**
   * Phase 9-04: route through `get-terms-signed-url` Edge Function (Option A
   * bucket gateway — see 09-CONTEXT.md "Storage Bucket Read Policies"). The
   * EF mints the signed URL with the service-role client, so the bucket
   * SELECT policy can stay admin-only. Default `false` to preserve the
   * direct-storage code path that 09-01 unit tests assume.
   */
  useEdgeFunction?: boolean;
  /**
   * Phase 9-04: when `useEdgeFunction === true`, requests a signed URL for a
   * SPECIFIC `terms_documents.id` (admin-only — investor callers MUST omit
   * this and the EF will return the `is_current = true` row). Used by the
   * Histórico download action in TermsHistoryTab.
   */
  termsDocumentId?: string;
  /**
   * Phase 9-04: when `useEdgeFunction === true`, forces the EF to mint a
   * signed URL for the EXACT `path` provided (admin-only escape hatch).
   * Used by the wizard preview where `path = tmp/{uuid}.pdf` exists in
   * storage but has no corresponding terms_documents row yet. Investor
   * viewers and history downloads MUST leave this `false` — the EF will
   * resolve `is_current = true` (default) or `terms_document_id` automatically.
   */
  useStoragePath?: boolean;
}

export interface SignedUrlHandle {
  /** Current signed URL. Snapshot at handle creation; call refresh() to rotate. */
  url: string;
  /** Forces a fresh signed URL; invokes onRenew(prevUrl) and returns the new URL. */
  refresh: () => Promise<string>;
}

// Module-level dedupe map: coalesces in-flight createSignedUrl calls keyed by
// `${bucket}:${path}:${ef?:0}:${docId??''}`. Cleared as soon as the underlying
// promise settles.
const inFlight = new Map<string, Promise<string>>();

function dedupeKey(
  bucket: string,
  path: string,
  useEf: boolean,
  termsDocumentId: string | undefined,
): string {
  return `${bucket}:${path}:${useEf ? 1 : 0}:${termsDocumentId ?? ''}`;
}

async function fetchFreshSignedUrl(
  bucket: string,
  path: string,
  expiresIn: number,
  useEdgeFunction: boolean,
  termsDocumentId: string | undefined,
  useStoragePath: boolean,
): Promise<string> {
  const key = dedupeKey(bucket, path, useEdgeFunction, termsDocumentId);
  const existing = inFlight.get(key);
  if (existing) return existing;

  const promise = (async () => {
    if (useEdgeFunction) {
      // Phase 9-04: route through EF gateway. Body shape mirrors
      // `supabase/functions/get-terms-signed-url/schema.ts`.
      //
      // Path resolution priority (EF side):
      //   1. `storage_path` (admin-only escape hatch — wizard tmp/ preview)
      //   2. `terms_document_id` (admin-only — Histórico download)
      //   3. is_current = true row (default — investor accept flow)
      //
      // We pass `storage_path` ONLY when explicitly opted-in via
      // `useStoragePath: true` (wizard preview before publish). Investor
      // viewers and history downloads MUST NOT forward `path` even though
      // it may start with `tmp/` — terms_documents.pdf_storage_path keeps
      // the original tmp/ value (08-02 decision: no rename on publish), and
      // forwarding it as storage_path would trigger the EF's admin gate.
      const body: { terms_document_id?: string; storage_path?: string; expires_in: number } = {
        expires_in: expiresIn,
      };
      if (termsDocumentId) body.terms_document_id = termsDocumentId;
      else if (useStoragePath) body.storage_path = path;

      const { data, error } = await supabase.functions.invoke('get-terms-signed-url', {
        body,
      });
      if (error) {
        // SECURITY: do not include `data` (may contain signed URL) in the
        // thrown error or any side-channel log.
        throw new Error('Failed to create signed URL');
      }
      // Edge Function shape: `{ ok: true, data: { signed_url, ... } }` OR
      // `{ ok: false, error: { code, message } }`. Supabase JS client returns
      // the parsed body verbatim in `data` when HTTP 2xx — we unwrap.
      const payload = data as
        | { ok: true; data: { signed_url: string } }
        | { ok: false; error: { code: string; message: string } };
      if (!payload || payload.ok !== true || !payload.data?.signed_url) {
        throw new Error('Failed to create signed URL');
      }
      return payload.data.signed_url;
    }

    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, expiresIn);
    if (error || !data?.signedUrl) {
      // SECURITY: do not include the URL or full path verbatim in the thrown
      // error or any side-channel log — the URL is a bearer credential and
      // the path can leak storage-layout details.
      throw new Error('Failed to create signed URL');
    }
    return data.signedUrl;
  })();

  inFlight.set(key, promise);
  try {
    return await promise;
  } finally {
    inFlight.delete(key);
  }
}

export async function createSignedUrlWithRenewal(
  opts: CreateSignedUrlOptions,
): Promise<SignedUrlHandle> {
  const {
    bucket,
    path,
    expiresIn,
    onRenew,
    useEdgeFunction = false,
    termsDocumentId,
    useStoragePath = false,
  } = opts;

  if (!Number.isFinite(expiresIn) || expiresIn < TTL_FLOOR_SECONDS) {
    throw new Error(
      `createSignedUrlWithRenewal: expiresIn must be >= ${TTL_FLOOR_SECONDS} seconds (2h floor per Storage decision)`,
    );
  }

  let currentUrl = await fetchFreshSignedUrl(
    bucket,
    path,
    expiresIn,
    useEdgeFunction,
    termsDocumentId,
    useStoragePath,
  );

  const refresh = async (): Promise<string> => {
    const prev = currentUrl;
    let next = await fetchFreshSignedUrl(
      bucket,
      path,
      expiresIn,
      useEdgeFunction,
      termsDocumentId,
      useStoragePath,
    );

    // Defensive cache-buster: if Supabase returns the identical token (e.g.
    // mocked test runner or deterministic backend), append a query param so
    // BUG-6 consumers always observe a different URL string and force a
    // re-fetch through the browser cache layer.
    if (next === prev) {
      const sep = next.includes('?') ? '&' : '?';
      next = `${next}${sep}_=${Date.now()}`;
    }

    // Invoke onRenew BEFORE updating the closure so the caller can capture
    // scroll position / consumer state attached to the old URL.
    onRenew?.(prev);
    currentUrl = next;
    return next;
  };

  return { url: currentUrl, refresh };
}
